import React, { useMemo, useState, useEffect } from 'react';
import { Header } from '../common_v2/components/Header';
import { Footer } from '../common_v2/components/Footer';
import { ApplicationDashboardStatus, ApplyApi, IDashboardApplicationItem } from '@apply/api/src/api/ApplyApi';
import { applicationInsights } from '../ApplicationInsights';
import { Greeting } from '../common_v2/components/Greeting';
import { useAuthentication } from '../LoginPage';
import { Dashboard } from './components/Dashboard';
import { isMobileScreen } from '@apply/ui';
import { MobileDashboard } from './components/MobileDashboard';
import { DrawApi } from '@apply/api';
import { IDashboardDrawBudget } from '@apply/api/src/api/DrawApi';

export const Home: React.FC = () => {
    const applyApi = new ApplyApi(applicationInsights);
    const mobileScreen = isMobileScreen();

    const { authentication } = useAuthentication();

    const [inProgressApplications, set_inProgressApplications] = useState<IDashboardApplicationItem[]>([]);
    const [applicationsPendingApproval, set_applicationsPendingApproval] = useState<IDashboardApplicationItem[]>([]);
    const [applicationsReadyToClose, set_applicationsReadyToClose] = useState<IDashboardApplicationItem[]>([]);
    const [dashboardDrawBudgets, set_dashboardDrawBudgets] = useState<IDashboardDrawBudget[]>([]);
    const [loading, set_loading] = useState<boolean>(false);

    const drawApi = useMemo(() => new DrawApi.DrawApi(applicationInsights), [applicationInsights]);

    const loadLoanData = async () => {
        set_loading(true);
        const response = await drawApi.dashboardDrawBudgets();
        if (response.statusCode !== 200) {
            set_loading(false);
            return;
        }
        const result = response.result;
        set_dashboardDrawBudgets(result);
        set_loading(false);
    };

    useEffect(() => {
        loadLoanData();
    }, []);

    const loadApplications = async () => {
        const response = await applyApi.dashboardApplications();

        if (response.statusCode !== 200) return;

        const [inProgress, submitted, readyToClose] = response.result.reduce(
            (
                [inProgress, submitted, readyToClose]: [
                    IDashboardApplicationItem[],
                    IDashboardApplicationItem[],
                    IDashboardApplicationItem[],
                ],
                application
            ) => {
                switch (application.applicationStatus) {
                    case ApplicationDashboardStatus.Draft:
                        inProgress.push(application);
                        break;
                    case ApplicationDashboardStatus.Submitted:
                        submitted.push(application);
                        break;
                    case ApplicationDashboardStatus.ReadyToClose:
                        readyToClose.push(application);
                        break;
                }

                return [inProgress, submitted, readyToClose];
            },
            [[], [], []]
        );

        set_inProgressApplications(inProgress);
        set_applicationsPendingApproval(submitted);
        set_applicationsReadyToClose(readyToClose);
    };

    useEffect(() => {
        loadApplications();
    }, []);

    const deleteApplication = async (applicationId: number) => {
        const response = await applyApi.deleteApplication(applicationId);

        if (response.statusCode !== 200) return;

        const newArray = inProgressApplications.filter((application) => application.applicationId !== applicationId);

        set_inProgressApplications(newArray);
    };

    return (
        <>
            <div className='app-container'>
                <Header />
                <main>
                    <div className={isMobileScreen() ? 'mobilePadding' : ''}>
                        <Greeting user={authentication?.user} />
                    </div>
                    {mobileScreen && (
                        <MobileDashboard
                            inProgressApplications={inProgressApplications}
                            applicationsPendingApproval={applicationsPendingApproval}
                            applicationsReadyToClose={applicationsReadyToClose}
                            deleteApplication={deleteApplication}
                            dashboardDrawBudgets={dashboardDrawBudgets}
                            loading={loading}
                        />
                    )}
                    {!mobileScreen && (
                        <Dashboard
                            inProgressApplications={inProgressApplications}
                            applicationsPendingApproval={applicationsPendingApproval}
                            applicationsReadyToClose={applicationsReadyToClose}
                            deleteApplication={deleteApplication}
                            dashboardDrawBudgets={dashboardDrawBudgets}
                            loading={loading}
                        />
                    )}
                </main>
            </div>
            <Footer />
        </>
    );
};
